/**
 * RoleList - Colonne gauche de la page Gestion des rôles.
 *
 * Affiche la liste des rôles existants avec :
 *   - Champ de recherche (filtre sur display_name)
 *   - Bouton "+ Créer un rôle" qui ouvre CreateRoleModal
 *   - Carte par rôle (pastille couleur, libellé, count membres, tags)
 *
 * Tri : super_admin (is_system) en tête, puis ordre alphabétique sur display_name.
 */

import { useState, useEffect, useCallback } from 'react';
import { PlusIcon, SearchIcon, RefreshIcon } from '../../icons';
import {
  RoleSummary,
  COLOR_PALETTE,
  DEFAULT_ROLE_COLOR,
  API_BASE,
  authHeaders
} from './types';
import CreateRoleModal from './CreateRoleModal';

interface RoleListProps {
  selectedRoleId: string | null;
  onSelect: (id: string) => void;
  reloadKey: number;
}

// Roles epingles aux bornes : non draggable, non drop-target.
// Doit rester aligne avec GLOBAL_ROLE_NAMES + UNDELETABLE_ROLE_NAMES backend.
const FIXED_ROLE_NAMES = new Set(['super_admin', 'technician']);

function RoleList({ selectedRoleId, onSelect, reloadKey }: RoleListProps) {
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[RoleList] Fetching /api/roles');
      const res = await fetch(`${API_BASE}/api/roles`, {
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      setRoles(json.roles || []);
      console.log(`[RoleList] ${(json.roles || []).length} rôle(s) chargé(s)`);
    } catch (err) {
      console.error('[RoleList] Erreur fetch:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles, reloadKey]);

  // Tri par position (mise a jour par drag-and-drop). super_admin epingle a 0,
  // technician en queue, autres roles entre les deux.
  const sortedRoles = [...roles].sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

  async function handleReorder(newOrderedIds: string[]) {
    setReordering(true);
    setError(null);
    try {
      console.log(`[RoleList] PUT /api/roles/reorder (${newOrderedIds.length} entries)`);
      const res = await fetch(`${API_BASE}/api/roles/reorder`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ orderedIds: newOrderedIds })
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      console.log('[RoleList] Reorder OK, rechargement');
      await fetchRoles();
    } catch (err) {
      console.error('[RoleList] Erreur reorder:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du reordonnancement');
      // Recharger pour annuler l'optimistic update
      await fetchRoles();
    } finally {
      setReordering(false);
    }
  }

  function handleDrop(targetId: string) {
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    // Construire le nouvel ordre des roles deplaçables
    const movable = sortedRoles.filter(r => !FIXED_ROLE_NAMES.has(r.name));
    const draggedIdx = movable.findIndex(r => r.id === draggedId);
    const targetIdx = movable.findIndex(r => r.id === targetId);
    if (draggedIdx === -1 || targetIdx === -1) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const reordered = [...movable];
    const [dragged] = reordered.splice(draggedIdx, 1);
    // Apres suppression du dragged, l'index de la cible se decale de 1
    // si le dragged etait au-dessus. Insert avant la cible dans tous les cas
    // (semantique "drop = au-dessus de la cible", coherente Discord-like).
    const insertIdx = draggedIdx < targetIdx ? targetIdx - 1 : targetIdx;
    reordered.splice(insertIdx, 0, dragged);

    // Optimistic update : mettre a jour les positions localement
    const newOrderedIds = reordered.map(r => r.id);
    setRoles(prev => prev.map(r => {
      if (FIXED_ROLE_NAMES.has(r.name)) return r;
      const newIdx = newOrderedIds.indexOf(r.id);
      return newIdx === -1 ? r : { ...r, position: newIdx + 1 };
    }));

    setDraggedId(null);
    setDragOverId(null);
    handleReorder(newOrderedIds);
  }

  const filteredRoles = sortedRoles.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.display_name || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: '#1e293b'
          }}>
            Rôles ({roles.length})
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={fetchRoles}
              title="Rafraîchir"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <RefreshIcon size={14} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              <PlusIcon size={14} color="white" />
              Créer
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div style={{ position: 'relative' }}>
          <SearchIcon
            size={14}
            color="#94a3b8"
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }}
          />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un rôle..."
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Liste */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
        maxHeight: 'calc(100vh - 280px)'
      }}>
        {loading && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            Chargement...
          </div>
        )}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            fontSize: '13px',
            margin: '8px'
          }}>
            {error}
          </div>
        )}
        {!loading && !error && filteredRoles.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            Aucun rôle trouvé.
          </div>
        )}
        {!loading && !error && filteredRoles.map(role => {
          const isSelected = role.id === selectedRoleId;
          const isHovered = hoveredId === role.id;
          const isFixed = FIXED_ROLE_NAMES.has(role.name);
          const isDragging = draggedId === role.id;
          const isDropTarget = dragOverId === role.id && draggedId !== role.id && !isFixed;
          const color = role.color || DEFAULT_ROLE_COLOR;
          // valider que la couleur est dans la palette ou un hex valide ; sinon fallback
          const safeColor = COLOR_PALETTE.includes(color) || /^#[0-9a-f]{6}$/i.test(color)
            ? color
            : DEFAULT_ROLE_COLOR;

          return (
            <div
              key={role.id}
              draggable={!isFixed && !reordering && !search.trim()}
              onDragStart={(e) => {
                if (isFixed) { e.preventDefault(); return; }
                setDraggedId(role.id);
                // Necessaire sur certains navigateurs pour activer le drag
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', role.id); } catch { /* IE */ }
              }}
              onDragOver={(e) => {
                if (isFixed || !draggedId || draggedId === role.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverId !== role.id) setDragOverId(role.id);
              }}
              onDragLeave={() => {
                if (dragOverId === role.id) setDragOverId(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (isFixed) return;
                handleDrop(role.id);
              }}
              onDragEnd={() => {
                setDraggedId(null);
                setDragOverId(null);
              }}
              onClick={() => onSelect(role.id)}
              onMouseEnter={() => setHoveredId(role.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                position: 'relative',
                padding: '10px 12px',
                marginBottom: '4px',
                cursor: isFixed ? 'pointer' : (isDragging ? 'grabbing' : 'pointer'),
                borderRadius: '6px',
                borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                borderTop: isDropTarget ? '2px solid #3b82f6' : '2px solid transparent',
                backgroundColor: isSelected
                  ? '#eff6ff'
                  : isHovered
                    ? '#f8fafc'
                    : 'transparent',
                opacity: isDragging ? 0.4 : 1,
                transition: 'background-color 0.15s, opacity 0.15s'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                {/* Poignee de drag (movable) ou cadenas (fixe) */}
                {isFixed ? (
                  <span
                    title={role.name === 'super_admin'
                      ? 'Rôle global épinglé en tête de liste'
                      : 'Rôle technicien épinglé en bas de liste'}
                    style={{
                      width: '14px',
                      fontSize: '11px',
                      color: '#cbd5e1',
                      flexShrink: 0,
                      textAlign: 'center'
                    }}
                  >
                    🔒
                  </span>
                ) : (
                  <span
                    title={search.trim() ? 'Désactiver la recherche pour réordonner' : 'Glisser pour réordonner'}
                    style={{
                      width: '14px',
                      color: search.trim() ? '#e2e8f0' : '#cbd5e1',
                      cursor: search.trim() ? 'not-allowed' : 'grab',
                      flexShrink: 0,
                      lineHeight: 1,
                      letterSpacing: '-2px',
                      fontSize: '14px',
                      userSelect: 'none'
                    }}
                  >
                    ⋮⋮
                  </span>
                )}
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: safeColor,
                  flexShrink: 0
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: isSelected ? 600 : 500,
                    color: '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {role.display_name}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                    marginTop: '2px'
                  }}>
                    <span
                      style={{ fontSize: '11px', color: '#94a3b8' }}
                      title={role.name === 'super_admin'
                        ? 'Compteur global : super-administrateurs de tous les établissements (rôle non rattaché à un périmètre)'
                        : 'Compteur scopé à l\'établissement courant'}
                    >
                      {role.member_count} membre{role.member_count > 1 ? 's' : ''}
                      {role.name === 'super_admin' && ' · global'}
                    </span>
                    {role.is_system && (
                      <span style={{
                        fontSize: '10px',
                        padding: '1px 6px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        borderRadius: '4px',
                        fontWeight: 500,
                        border: '1px solid #fcd34d'
                      }}>
                        système
                      </span>
                    )}
                    {role.is_default && (
                      <span style={{
                        fontSize: '10px',
                        padding: '1px 6px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: '4px',
                        fontWeight: 500,
                        border: '1px solid #93c5fd'
                      }}>
                        par défaut
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(newId) => {
            setShowCreateModal(false);
            fetchRoles();
            onSelect(newId);
          }}
        />
      )}
    </div>
  );
}

export default RoleList;
